import React, { useEffect, useMemo, useState } from 'react';
import {
  FiActivity,
  FiAlertCircle,
  FiBarChart2,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiCommand,
  FiLayers,
  FiPlay,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiX,
} from 'react-icons/fi';
import { analyticsService, Agent } from '../services/analyticsService';
import { eventService } from '../services/eventService';
import { chatService } from '../services/chatService';
import { ChatMessage } from '../components/chat/ChatMessage';
import { ChatLoadingIndicator } from '../components/chat/ChatLoadingIndicator';
import { useTitle } from '../contexts/TitleContext';

interface ComparisonResult {
  agent: string;
  chatId?: number;
  messages?: any[];
  totalCostUsd?: number;
  loading: boolean;
  processing?: boolean;
  error?: boolean;
}

type AgentFilter = 'all' | Agent['category'];
type AgentSort = 'usage' | 'name';

const CATEGORY_OPTIONS: Array<{ id: AgentFilter; label: string }> = [
  { id: 'all', label: 'همه' },
  { id: 'shopping', label: 'خرید' },
  { id: 'model', label: 'مدل‌ها' },
  { id: 'architecture', label: 'معماری‌ها' },
  { id: 'research', label: 'تحقیق' },
  { id: 'search', label: 'جست‌وجو' },
  { id: 'general', label: 'عمومی' },
];

const CATEGORY_LABELS: Record<Agent['category'], string> = {
  shopping: 'خرید',
  model: 'مدل ثابت',
  architecture: 'معماری',
  research: 'تحقیق',
  search: 'جست‌وجو',
  general: 'عمومی',
};

const numberFormatter = new Intl.NumberFormat('fa-IR');

const formatCost = (cost?: number) => {
  if (cost === undefined) return '—';
  return `$${cost.toLocaleString('en-US', {
    minimumFractionDigits: 6,
    maximumFractionDigits: 8,
  })}`;
};

const wait = (milliseconds: number) => new Promise((resolve) => {
  window.setTimeout(resolve, milliseconds);
});

const AgentCompare = () => {
  const { setTitle } = useTitle();
  const [message, setMessage] = useState('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [results, setResults] = useState<Record<string, ComparisonResult>>({});
  const [lastRunAgents, setLastRunAgents] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<AgentFilter>('all');
  const [sortBy, setSortBy] = useState<AgentSort>('usage');
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [agentsError, setAgentsError] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  useEffect(() => {
    setTitle('مقایسه دستیارها');
  }, [setTitle]);

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      setAgentsError(false);
      setAgents(await analyticsService.getAgents());
    } catch (error) {
      console.error('Failed to fetch agents', error);
      setAgentsError(true);
    } finally {
      setLoadingAgents(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (!isPickerOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsPickerOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPickerOpen]);

  const agentById = useMemo(
    () => new Map(agents.map((agent) => [agent.id, agent])),
    [agents],
  );

  const visibleAgents = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    return agents
      .filter((agent) => activeCategory === 'all' || agent.category === activeCategory)
      .filter((agent) => {
        if (!normalizedSearch) return true;
        return [agent.name, agent.id, agent.description]
          .some((value) => value.toLocaleLowerCase().includes(normalizedSearch));
      })
      .sort((a, b) => {
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        return b.usage_24h - a.usage_24h || a.name.localeCompare(b.name);
      });
  }, [activeCategory, agents, search, sortBy]);

  const popularAgents = useMemo(
    () => [...agents]
      .sort((a, b) => b.usage_24h - a.usage_24h || a.name.localeCompare(b.name))
      .slice(0, 3)
      .map((agent) => agent.id),
    [agents],
  );

  const completedCount = useMemo(
    () => lastRunAgents.filter((agentId) => {
      const result = results[agentId];
      return result && !result.loading && !result.error;
    }).length,
    [lastRunAgents, results],
  );

  const totalCost = useMemo(
    () => lastRunAgents.reduce(
      (total, agentId) => total + (results[agentId]?.totalCostUsd || 0),
      0,
    ),
    [lastRunAgents, results],
  );

  const toggleAgent = (agentId: string) => {
    setSelectedAgents((current) => current.includes(agentId)
      ? current.filter((id) => id !== agentId)
      : [...current, agentId]);
  };

  const runComparison = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || selectedAgents.length === 0 || isRunning) return;

    const agentsForRun = [...selectedAgents];
    const initialResults = agentsForRun.reduce<Record<string, ComparisonResult>>((next, agentId) => {
      next[agentId] = { agent: agentId, loading: true };
      return next;
    }, {});

    setIsRunning(true);
    setLastRunAgents(agentsForRun);
    setResults(initialResults);

    await Promise.all(agentsForRun.map(async (agentId) => {
      try {
        const data = await analyticsService.runComparison(trimmedMessage, agentId);
        const history = await chatService.fetchChatHistory(data.chat_id);
        setResults((current) => ({
          ...current,
          [agentId]: {
            agent: agentId,
            chatId: data.chat_id,
            messages: history.history,
            totalCostUsd: data.total_cost_usd,
            loading: false,
          },
        }));
      } catch (error) {
        console.error(`Comparison failed for ${agentId}`, error);
        setResults((current) => ({
          ...current,
          [agentId]: { agent: agentId, loading: false, error: true },
        }));
      }
    }));

    setIsRunning(false);
  };

  const handleProductClick = async (chatId: number | undefined, productId: number) => {
    if (!chatId) return;
    void eventService.captureProductClick(chatId, productId, 'pdp');
  };

  const handleSendMessage = async (agentId: string, chatId: number | undefined, messageContent: string) => {
    if (!chatId || !messageContent.trim() || results[agentId]?.processing) return;

    const optimisticMessage = {
      id: `optimistic-${Date.now()}`,
      type: 'user',
      content: messageContent,
      created_at: new Date().toISOString(),
      isSending: true,
    };
    setResults((current) => ({
      ...current,
      [agentId]: {
        ...current[agentId],
        processing: true,
        messages: [...(current[agentId]?.messages || []), optimisticMessage],
      },
    }));

    try {
      await chatService.sendMessage(chatId, messageContent);

      for (let attempt = 0; attempt < 60; attempt += 1) {
        await wait(attempt === 0 ? 500 : 2000);
        const history = await chatService.fetchChatHistory(chatId);
        const isProcessing = history.status === 'pending' || history.status === 'processing';
        setResults((current) => ({
          ...current,
          [agentId]: {
            ...current[agentId],
            messages: history.history,
            processing: isProcessing,
          },
        }));
        if (!isProcessing) return;
      }

      setResults((current) => ({
        ...current,
        [agentId]: { ...current[agentId], processing: false },
      }));
    } catch (error) {
      console.error(`Failed to continue comparison chat ${chatId}`, error);
      setResults((current) => ({
        ...current,
        [agentId]: { ...current[agentId], processing: false },
      }));
    }
  };

  const renderResultBody = (agentId: string) => {
    const result = results[agentId];

    if (!result || result.loading) {
      return (
        <div className="flex h-full flex-col items-center justify-center px-6 text-center">
          <span className="h-10 w-10 rounded-full border-[3px] border-indigo-100 border-t-brand-navy animate-spin dark:border-indigo-950 dark:border-t-indigo-300" />
          <p className="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-200">در حال آماده‌سازی جواب...</p>
          <p className="mt-1 text-[11px] text-gray-400">نتیجه این بخش جداگانه به‌روز می‌شه.</p>
        </div>
      );
    }

    if (result.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center px-6 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-500 dark:bg-red-950/30">
            <FiAlertCircle size={24} />
          </span>
          <p className="mt-4 text-sm font-semibold text-gray-800 dark:text-gray-100">این دستیار اجرا نشد</p>
          <p className="mt-1 text-[11px] leading-5 text-gray-400">برای اینکه دوباره امتحان کنی، مقایسه رو از نو اجرا کن.</p>
        </div>
      );
    }

    if (result.messages && result.messages.length > 0) {
      return (
        <div className="text-gray-800 dark:text-gray-100">
          {result.messages.map((message, index) => {
            const isLastAgentMessage = message.type === 'agent'
              && !result.messages!.slice(index + 1).some((nextMessage) => nextMessage.type === 'user');
            return (
              <ChatMessage
                key={message.id || `${result.chatId}-${index}`}
                message={message}
                onProductClick={(productId) => {
                  void handleProductClick(result.chatId, productId);
                }}
                onSendMessage={(messageContent) => {
                  void handleSendMessage(agentId, result.chatId, messageContent);
                }}
                isLastAgentMessage={isLastAgentMessage}
                agent_chat_id={result.chatId}
              />
            );
          })}
          {result.processing && <ChatLoadingIndicator />}
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center text-gray-400">
        <FiAlertCircle size={24} />
        <p className="mt-3 text-sm">هیچ جوابی برای نمایش پیدا نشد.</p>
      </div>
    );
  };

  const renderAgentPicker = () => {
    if (!isPickerOpen) return null;

    return (
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-sm md:p-6"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setIsPickerOpen(false);
        }}
      >
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="agent-picker-title"
          className="agent-picker-v3 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl dark:bg-gray-900"
        >
          <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 dark:border-gray-800 md:px-6">
            <div>
              <div className="flex items-center gap-2">
                <h2 id="agent-picker-title" className="text-lg font-black text-gray-900 dark:text-white">انتخاب دستیارها</h2>
                <span className="rounded-full bg-brand-orange/10 px-2 py-0.5 text-[11px] font-bold text-brand-orange">
                  {numberFormatter.format(selectedAgents.length)} انتخاب
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">هر چند تا دستیار که می‌خوای انتخاب کن؛ همه با یه پیام اجرا می‌شن.</p>
            </div>
            <button
              type="button"
              onClick={() => setIsPickerOpen(false)}
              className="rounded-xl p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              aria-label="بستن"
            >
              <FiX size={20} />
            </button>
          </header>

          <div className="border-b border-gray-100 px-5 py-3 dark:border-gray-800 md:px-6">
            <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_190px_auto]">
              <label className="relative block">
                <FiSearch className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  autoFocus
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="جست‌وجو با عنوان، شناسه یا توضیحات..."
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-4 pr-10 text-sm text-gray-900 outline-none transition focus:border-brand-orange focus:bg-white focus:ring-4 focus:ring-brand-orange/10 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </label>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value as AgentSort)}
                className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs font-medium text-gray-700 outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
              >
                <option value="usage">پراستفاده‌ترین</option>
                <option value="name">بر اساس عنوان</option>
              </select>
              <button
                type="button"
                onClick={() => setSelectedAgents(popularAgents)}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-indigo-50 px-3 py-2.5 text-xs font-bold text-brand-navy transition hover:bg-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-200"
              >
                <FiBarChart2 /> ۳ دستیار پرکاربرد
              </button>
            </div>

            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {CATEGORY_OPTIONS.map((category) => {
                const count = category.id === 'all'
                  ? agents.length
                  : agents.filter((agent) => agent.category === category.id).length;
                if (category.id !== 'all' && count === 0) return null;

                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveCategory(category.id)}
                    className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                      activeCategory === category.id
                        ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {category.label} <span className="opacity-60">{numberFormatter.format(count)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/60 p-4 dark:bg-gray-950/30 md:p-5">
            {loadingAgents ? (
              <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                  <div key={item} className="h-28 animate-pulse rounded-2xl bg-gray-200/60 dark:bg-gray-800" />
                ))}
              </div>
            ) : agentsError ? (
              <div className="flex min-h-72 flex-col items-center justify-center text-center">
                <FiAlertCircle size={28} className="text-red-500" />
                <p className="mt-3 text-sm font-semibold text-gray-800 dark:text-gray-100">فهرست دستیارها بارگذاری نشد.</p>
                <button type="button" onClick={fetchAgents} className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand-orange">
                  <FiRefreshCw /> تلاش دوباره
                </button>
              </div>
            ) : visibleAgents.length === 0 ? (
              <div className="flex min-h-72 flex-col items-center justify-center text-center text-gray-400">
                <FiSearch size={28} />
                <p className="mt-3 text-sm">دستیاری با این مشخصات پیدا نشد.</p>
                <button type="button" onClick={() => { setSearch(''); setActiveCategory('all'); }} className="mt-2 text-xs font-bold text-brand-orange">حذف فیلترها</button>
              </div>
            ) : (
              <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                {visibleAgents.map((agent) => {
                  const isSelected = selectedAgents.includes(agent.id);
                  return (
                    <button
                      key={agent.id}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => toggleAgent(agent.id)}
                      className={`group flex min-h-[112px] flex-col rounded-2xl border p-3.5 text-right transition-all ${
                        isSelected
                          ? 'border-brand-orange bg-orange-50 shadow-sm ring-2 ring-brand-orange/10 dark:bg-orange-950/20'
                          : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:hover:border-indigo-700'
                      }`}
                    >
                      <div className="flex w-full items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-xl dark:bg-gray-800">{agent.avatar}</span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="truncate text-sm font-bold text-gray-900 dark:text-white">{agent.name}</h3>
                            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${isSelected ? 'border-brand-orange bg-brand-orange text-white' : 'border-gray-300 text-transparent dark:border-gray-600'}`}>
                              <FiCheck size={12} />
                            </span>
                          </div>
                          <p dir="ltr" className="mt-0.5 truncate text-left font-mono text-[9px] text-gray-400">{agent.id}</p>
                        </div>
                      </div>
                      <p className="mt-2 line-clamp-1 text-[10px] leading-5 text-gray-500 dark:text-gray-400">{agent.description}</p>
                      <div className="mt-auto flex items-center justify-between gap-2 pt-2 text-[10px]">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-300">{CATEGORY_LABELS[agent.category]}</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-gray-600 dark:text-gray-300">
                          <FiClock className="text-emerald-500" /> {numberFormatter.format(agent.usage_24h)} اجرا / ۲۴ ساعت
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <footer className="flex flex-col gap-3 border-t border-gray-100 bg-white px-5 py-3.5 dark:border-gray-800 dark:bg-gray-900 sm:flex-row sm:items-center sm:justify-between md:px-6">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2 space-x-reverse">
                {selectedAgents.slice(0, 6).map((agentId) => (
                  <span key={agentId} className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gray-100 text-sm dark:border-gray-900 dark:bg-gray-800" title={agentById.get(agentId)?.name}>
                    {agentById.get(agentId)?.avatar || '🤖'}
                  </span>
                ))}
              </div>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                {selectedAgents.length === 0 ? 'هیچ دستیاری انتخاب نشده' : `${numberFormatter.format(selectedAgents.length)} دستیار برای مقایسه`}
              </span>
              {selectedAgents.length > 0 && (
                <button type="button" onClick={() => setSelectedAgents([])} className="text-[11px] text-gray-400 hover:text-red-500">پاک کن</button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsPickerOpen(false)}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-gray-900 px-5 py-2.5 text-xs font-bold text-white transition hover:bg-gray-800 disabled:opacity-40 dark:bg-white dark:text-gray-900"
            >
              <FiCheckCircle /> تأیید
            </button>
          </footer>
        </section>
      </div>
    );
  };

  return (
    <div dir="rtl" className="ds-scrollbar h-full overflow-y-auto bg-ds-canvas">
      <div className="mx-auto max-w-[1120px] px-3 py-5 app-sm:px-6 app-sm:py-6">
        <header className="mb-5 flex flex-col gap-4 app-sm:flex-row app-sm:items-center app-sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-ds-12 bg-ds-primary-soft text-ds-primary">
              <FiActivity size={21} />
            </span>
            <div>
              <h1 className="text-xl font-black tracking-tight text-ds-ink">مقایسه هم‌زمان دستیارها</h1>
              <p className="mt-1 text-[11px] text-ds-500">یک پرسش رو با دستیارهای انتخابی اجرا کن و پاسخ‌ها رو کنار هم ببین.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-ds-100 bg-ds-white px-3 py-1.5 font-medium text-ds-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> محیط توسعه
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-ds-100 bg-ds-white px-3 py-1.5 text-ds-500">
              <FiClock /> آمار ۲۴ ساعت اخیر
            </span>
          </div>
        </header>

        <section className="overflow-hidden rounded-ds-16 border border-ds-100 bg-ds-white">
          <div className="grid lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.55fr)]">
            <div className="p-4 app-sm:p-6 app-lg:border-l app-lg:border-ds-100">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-sm font-bold text-ds-ink">پرسش</h2>
                  <p className="mt-1 text-[11px] text-ds-500">برای اجرای سریع‌تر، از Ctrl + Enter استفاده کن.</p>
                </div>
                <span className="hidden items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 font-mono text-[10px] text-gray-400 sm:inline-flex dark:border-gray-700">
                  <FiCommand /> Enter
                </span>
              </div>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                    event.preventDefault();
                    runComparison();
                  }
                }}
                rows={7}
                placeholder="سؤالی بنویس که می‌خوای همه دستیارها با شرایط یکسان بهش جواب بدن…"
                className="mt-4 min-h-[180px] w-full resize-y rounded-ds-12 border border-ds-200 bg-ds-canvas p-4 text-sm leading-7 text-ds-ink outline-none placeholder:text-ds-400 focus:border-ds-primary focus:bg-ds-white focus:ring-[3px] focus:ring-ds-primary-ring"
              />
            </div>

            <aside className="flex min-h-[300px] flex-col bg-ds-canvas p-4 app-sm:p-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-bold text-ds-ink">دستیارهای انتخاب‌شده</h2>
                  <p className="mt-1 text-[11px] text-ds-500">هر دستیار یک پاسخ جداگانه تولید می‌کنه.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPickerOpen(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-ds-6 bg-ds-primary-soft px-3 text-xs font-bold text-ds-primary-strong hover:bg-ds-primary-ring"
                >
                  <FiPlus /> افزودن دستیار
                </button>
              </div>

              <div className="mt-4 min-h-0 flex-1">
                {selectedAgents.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => setIsPickerOpen(true)}
                    className="flex h-full min-h-[180px] w-full flex-col items-center justify-center rounded-ds-12 border border-dashed border-ds-200 px-5 text-center transition hover:border-ds-primary hover:bg-ds-primary-soft"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-brand-orange shadow-sm dark:bg-gray-800"><FiPlus size={21} /></span>
                    <span className="mt-3 text-sm font-bold text-gray-700 dark:text-gray-200">دستیارهای مقایسه رو انتخاب کن</span>
                    <span className="mt-1 text-[11px] text-gray-400">جست‌وجو، فیلتر و آمار استفاده در مرحله بعد</span>
                  </button>
                ) : (
                  <div className="max-h-[210px] space-y-1.5 overflow-y-auto pl-1">
                    {selectedAgents.map((agentId, index) => {
                      const agent = agentById.get(agentId);
                      return (
                        <div key={agentId} className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-2.5 py-2 dark:border-gray-700 dark:bg-gray-800">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-base dark:bg-gray-700">{agent?.avatar || '🤖'}</span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-bold text-gray-800 dark:text-gray-100">{agent?.name || agentId}</p>
                            <p className="mt-0.5 text-[9px] text-gray-400">ستون {numberFormatter.format(index + 1)}</p>
                          </div>
                          <button type="button" onClick={() => toggleAgent(agentId)} className="rounded-lg p-1.5 text-gray-300 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/30" aria-label={`${agent?.name || agentId} رو حذف کن`}>
                            <FiX />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </aside>
          </div>

          <div className="flex flex-col gap-3 border-t border-ds-100 bg-ds-white px-4 py-4 app-sm:flex-row app-sm:items-center app-sm:justify-between app-sm:px-6">
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1.5 font-bold text-gray-700 dark:text-gray-200">
                <FiLayers /> {numberFormatter.format(selectedAgents.length)} دستیار
              </span>
              <span className="h-3 w-px bg-gray-200 dark:bg-gray-700" />
              <span>هر انتخاب یک اجرای واقعی و جداگانه‌ست.</span>
            </div>
            <button
              type="button"
              onClick={runComparison}
              disabled={!message.trim() || selectedAgents.length === 0 || isRunning}
              className="inline-flex h-10 min-w-[220px] items-center justify-center gap-2 rounded-ds-6 bg-ds-primary px-5 text-[13px] font-bold text-white transition hover:bg-ds-primary-strong disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isRunning ? <FiRefreshCw className="animate-spin" /> : <FiPlay />}
              {isRunning ? `در حال اجرای ${numberFormatter.format(selectedAgents.length)} دستیار…` : 'شروع مقایسه'}
            </button>
          </div>
        </section>

        <section className="mt-6">
          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-base font-black text-gray-900 dark:text-white">نتیجه مقایسه</h2>
              <p className="mt-1 text-xs text-gray-400">
                {lastRunAgents.length === 0
                  ? 'بعد از اجرا، پاسخ تمام دستیارها اینجا کنار هم قرار می‌گیره.'
                  : `${numberFormatter.format(completedCount)} از ${numberFormatter.format(lastRunAgents.length)} پاسخ آماده‌ست.`}
              </p>
            </div>
            {lastRunAgents.length > 0 && (
              <div className="flex items-center gap-2 text-[11px]">
                <span className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 font-medium text-brand-navy dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-200">
                  عرض واقعی صفحه گفت‌وگو
                </span>
                <span className="rounded-xl border border-gray-200 bg-white px-3 py-2 font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                  {numberFormatter.format(lastRunAgents.length)} ستون مقایسه
                </span>
                <span dir="ltr" className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 font-bold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
                  Total {formatCost(totalCost)} USD
                </span>
              </div>
            )}
          </div>

          {lastRunAgents.length === 0 ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-ds-16 border border-dashed border-ds-200 bg-ds-white px-6 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-brand-orange dark:bg-orange-950/40 dark:text-orange-300"><FiLayers size={23} /></span>
              <p className="mt-4 text-sm font-bold text-gray-700 dark:text-gray-200">هنوز مقایسه‌ای انجام نشده.</p>
              <p className="mt-1 text-xs text-gray-400">پیام رو بنویس، دستیارها رو انتخاب کن و «شروع مقایسه» رو بزن.</p>
            </div>
          ) : (
            <div className="overflow-x-auto pb-3">
              <div
                className="grid gap-3"
                style={{
                  gridTemplateColumns: `repeat(${lastRunAgents.length}, minmax(420px, 1fr))`,
                  minWidth: `${lastRunAgents.length * 420}px`,
                  justifyContent: lastRunAgents.length === 1 ? 'center' : 'start',
                }}
              >
                {lastRunAgents.map((agentId, index) => {
                  const agent = agentById.get(agentId);
                  const result = results[agentId];
                  return (
                    <article key={agentId} className="flex h-[680px] min-w-0 flex-col overflow-hidden rounded-ds-16 border border-ds-100 bg-ds-white shadow-ds-1">
                      <header className="border-b border-gray-100 px-4 py-3.5 dark:border-gray-800">
                        <div className="flex items-center gap-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-xl dark:bg-gray-800">{agent?.avatar || '🤖'}</span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="flex h-5 min-w-5 items-center justify-center rounded-md bg-gray-900 px-1 text-[9px] font-bold text-white dark:bg-white dark:text-gray-900">{numberFormatter.format(index + 1)}</span>
                              <h3 className="truncate text-sm font-black text-gray-900 dark:text-white">{agent?.name || agentId}</h3>
                            </div>
                            <p dir="ltr" className="mt-0.5 truncate text-left font-mono text-[9px] text-gray-400">{agentId}</p>
                          </div>
                          {result?.loading ? (
                            <span className="h-5 w-5 rounded-full border-2 border-indigo-100 border-t-brand-navy animate-spin dark:border-indigo-950 dark:border-t-indigo-300" />
                          ) : result?.error ? (
                            <FiAlertCircle className="text-red-500" />
                          ) : (
                            <FiCheckCircle className="text-emerald-500" />
                          )}
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                          <div className="rounded-lg bg-gray-50 px-2.5 py-2 dark:bg-gray-800">
                            <p className="text-gray-400">شناسه گفت‌وگو</p>
                            <p className="mt-0.5 font-bold text-gray-700 dark:text-gray-200">{result?.chatId ? numberFormatter.format(result.chatId) : '—'}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 px-2.5 py-2 dark:bg-gray-800">
                            <p className="text-gray-400">هزینه اجرا</p>
                            <p dir="ltr" className="mt-0.5 truncate text-left font-bold text-emerald-600 dark:text-emerald-400">{result?.loading ? 'Calculating…' : `${formatCost(result?.totalCostUsd)} USD`}</p>
                          </div>
                        </div>
                      </header>
                      <div className="min-h-0 flex-1 overflow-y-auto p-4">
                        {renderResultBody(agentId)}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>

      {renderAgentPicker()}
    </div>
  );
};

export default AgentCompare;
